// Auto-generated: serves the NOTICE_UTILISATION.md content at /aide
// Users access markdown documentation in-app.
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, BookOpen, Download, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Minimal markdown renderer reused from CallScriptDialog
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split('\n');
  const out = [];
  let listBuf = [];
  let tableBuf = null;
  const flushList = (key) => {
    if (listBuf.length) { out.push(<ul key={`ul-${key}`} className="list-disc pl-6 space-y-1 mb-3">{listBuf}</ul>); listBuf = []; }
  };
  const flushTable = (key) => {
    if (tableBuf && tableBuf.length >= 2) {
      const [header, sep, ...rows] = tableBuf;
      const headers = header.split('|').map(c => c.trim()).filter(Boolean);
      out.push(
        <div key={`tbl-${key}`} className="overflow-x-auto mb-3">
          <table className="text-sm border-collapse border border-[#E5E7EB] w-full">
            <thead className="bg-[#F4F4F5]"><tr>{headers.map((h,i) => <th key={i} className="px-2 py-1 border border-[#E5E7EB] text-left">{h}</th>)}</tr></thead>
            <tbody>{rows.map((r, i) => {
              const cells = r.split('|').map(c => c.trim()).filter((_, idx, arr) => idx > 0 && idx < arr.length);
              return <tr key={i} className="even:bg-[#FAFAFA]">{cells.map((c, j) => <td key={j} className="px-2 py-1 border border-[#E5E7EB]" dangerouslySetInnerHTML={{ __html: c.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }}/>)}</tr>;
            })}</tbody>
          </table>
        </div>
      );
    }
    tableBuf = null;
  };
  const inline = (s) => s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="bg-[#F4F4F5] px-1 rounded text-[0.85em]">$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#F97316] underline" target="_blank">$1</a>');
  lines.forEach((line, i) => {
    if (line.startsWith('|') && line.endsWith('|')) { flushList(i); if (!tableBuf) tableBuf = []; tableBuf.push(line); return; }
    flushTable(i);
    if (!line.trim()) { flushList(i); return; }
    if (line.startsWith('### ')) { flushList(i); out.push(<h3 key={i} className="text-base font-bold mt-4 mb-2 text-[#18181B]">{line.slice(4)}</h3>); return; }
    if (line.startsWith('## ')) { flushList(i); out.push(<h2 key={i} className="text-lg font-bold mt-5 mb-2 text-[#F97316]">{line.slice(3)}</h2>); return; }
    if (line.startsWith('# ')) { flushList(i); out.push(<h1 key={i} className="text-2xl font-bold mt-6 mb-3">{line.slice(2)}</h1>); return; }
    if (line.startsWith('---')) { flushList(i); out.push(<hr key={i} className="my-4 border-[#E5E7EB]"/>); return; }
    if (line.startsWith('> ')) { flushList(i); out.push(<blockquote key={i} className="border-l-4 border-[#F97316] pl-3 py-1 my-2 text-sm text-[#71717A] italic">{line.slice(2)}</blockquote>); return; }
    if (/^\s*[-*]\s/.test(line) || /^\s*\d+\.\s/.test(line)) {
      const content = line.replace(/^\s*(?:[-*]|\d+\.)\s/, '');
      listBuf.push(<li key={`li-${i}`} className="text-sm" dangerouslySetInnerHTML={{ __html: inline(content) }}/>);
      return;
    }
    flushList(i);
    out.push(<p key={i} className="text-sm mb-2 leading-relaxed text-[#27272A]" dangerouslySetInnerHTML={{ __html: inline(line) }}/>);
  });
  flushList('end'); flushTable('end');
  return out;
}

export default function HelpPage() {
  const [md, setMd] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch the notice file from public/
    fetch('/NOTICE_UTILISATION.md')
      .then(r => r.text())
      .then(t => { setMd(t); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const download = () => {
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'WASABI-ProspecTel-Notice.md';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="help-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-[#F97316]"/>Notice d'utilisation
          </h1>
          <p className="text-xs text-[#71717A]">Guide complet de toutes les fonctionnalités</p>
        </div>
        <Button onClick={download} variant="outline" size="sm" data-testid="download-notice-btn">
          <Download className="w-4 h-4 mr-1"/>Télécharger
        </Button>
      </div>
      <Card>
        <CardContent className="p-8 prose prose-sm max-w-none">
          {loading ? (
            <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#F97316]"/></div>
          ) : md ? (
            renderMarkdown(md)
          ) : (
            <p className="text-sm text-[#71717A]">Notice introuvable. Contactez votre administrateur.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
